// The Models view of Inference (ui-spec §5.10): Indra, the routed model, featured on its own card, then the
// on-demand catalog as a grid of cards, 1 to 3 columns by the width the page has, filtered by search and capability.
// A card opens the playground (playground.tsx) with that model selected.
import { ArrowRight, Brain, Eye, GitBranch, Headphones, Layers, Wrench, type LucideIcon } from "lucide-react";
import { Button } from "@nodus/ui";
import { modelPublisher } from "./model-publisher";
import "./model-publisher.css";
import { LabLogo } from "./lab-logo";
import { apiLabel, CAPABILITIES, CAPABILITY_LABEL, cardPriceLabel, modalityLabel, modelTitle, tokenCountLabel, type Capability, type CatalogModel } from "./model";

const CAPABILITY_ICON: Record<Capability, LucideIcon> = { vision: Eye, tools: Wrench, reasoning: Brain, decisions: GitBranch, audio: Headphones, embeddings: Layers };

/** The model's capabilities as plain text, "Vision · Tools · Reasoning". */
function capabilityText(model: CatalogModel): string {
  return CAPABILITIES.filter((c) => model.capabilities?.[c])
    .map((c) => CAPABILITY_LABEL[c])
    .join(" · ");
}

/** The capability toggles beside the search: each one narrows the grid to models that have it. */
export function CapabilityFilter({ value, onChange }: { value: Capability[]; onChange: (caps: Capability[]) => void }) {
  return (
    <div role="group" aria-label="Capabilities" className="flex flex-wrap items-center gap-1.5">
      {CAPABILITIES.map((c) => {
        const on = value.includes(c);
        const Icon = CAPABILITY_ICON[c];
        return (
          <button
            key={c}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((x) => x !== c) : CAPABILITIES.filter((x) => x === c || value.includes(x)))}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-line px-3 text-small text-fg-muted transition-colors hover:border-line-strong hover:text-fg-primary aria-pressed:border-line-selected aria-pressed:bg-selected aria-pressed:text-fg-primary"
          >
            <Icon className="size-3.5" strokeWidth={2} aria-hidden />
            {CAPABILITY_LABEL[c]}
          </button>
        );
      })}
    </div>
  );
}

/** Modality and API stay readable without repeating bright badges on every card. */
function Chips({ model }: { model: CatalogModel }) {
  return (
    <ul aria-label="Modality and API" className="flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-small text-fg-muted">
      <li>{modalityLabel(model)}</li>
      <li>{apiLabel(model)}</li>
      {model.preview ? <li className="font-medium text-fg-secondary">Preview</li> : null}
    </ul>
  );
}

function ModelCard({ model, onTry }: { model: CatalogModel; onTry: () => void }) {
  const title = modelTitle(model);
  const price = cardPriceLabel(model);
  const caps = capabilityText(model);
  const publisher = modelPublisher(model.id);
  return (
    <li style={publisher.style} className="publisher-card relative flex min-w-0 flex-col rounded-lg border border-line bg-card p-4 transition-colors hover:border-line-strong hover:bg-hover">
      <div className="flex items-center gap-3">
        <LabLogo id={model.id} className="publisher-logo size-8" />
        <div className="min-w-0 flex-1">
          <span className="publisher-label">{publisher.label}</span>
          <h3 className="break-words text-[16px] leading-6 font-semibold tracking-[var(--nd-tracking-display)] text-fg-primary">{title}</h3>
        </div>
      </div>
      <code title={model.id} className="mt-2 block truncate font-mono text-small text-fg-muted">{model.id}</code>
      <div className="mt-3">
        <Chips model={model} />
      </div>
      <p className="mt-2 min-h-4 text-small text-fg-muted">{caps}</p>
      <div className="flex-1" />
      <div className="mt-3 border-t border-line-subtle pt-3">
        <p className="text-small leading-5 text-fg-secondary tabular-nums">{price ?? "Pricing not reported"}</p>
        <div className="mt-2 flex items-center justify-between gap-3 text-small">
          <span className="text-fg-muted">{model.context_window ? `${tokenCountLabel(model.context_window)} context` : ""}</span>
          <span className="inline-flex items-center gap-1.5 text-fg-primary" aria-hidden>Open in Playground <ArrowRight className="size-3.5" /></span>
        </div>
      </div>
      <button type="button" onClick={onTry} aria-label={`Try ${title}`} className="absolute inset-0 cursor-pointer rounded-lg" />
    </li>
  );
}

/** Indra on its own card: its name and what it can do; the whole card opens the playground. */
export function FeaturedModel({ model, onTry }: { model: CatalogModel; onTry: () => void }) {
  return (
    <section aria-label="Automatic routing" className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-line-subtle bg-subtle/50 px-4 py-3">
      <LabLogo id={model.id} className="size-7" />
      <p className="min-w-0 flex-1 text-small text-fg-muted"><span className="font-medium text-fg-primary">Not sure where to start?</span> Let {modelTitle(model)} choose a model for you.</p>
      <Button type="button" variant="ghost" size="sm" onClick={onTry} aria-label={`Try ${modelTitle(model)}`}>Try automatic routing <ArrowRight className="size-3.5" aria-hidden /></Button>
    </section>
  );
}

/** The on-demand catalog: 1 to 3 columns by the container's width (not the viewport's, so the sidebar's state counts). */
export function ModelGrid({ models, onTry }: { models: CatalogModel[]; onTry: (id: string) => void }) {
  return (
    <div className="@container">
      <ul aria-label="Models" data-focus-inset className="grid grid-cols-1 gap-4 @min-[34rem]:grid-cols-2 @min-[56rem]:grid-cols-3">
        {models.map((m) => (
          <ModelCard key={m.id} model={m} onTry={() => onTry(m.id)} />
        ))}
      </ul>
    </div>
  );
}
